// admin-web — operational admin client contract (doc 30 §2, §4, §5).
//
// This module is the THIN admin-web-specific surface. The actual typed
// service client and route table live in `@veluga/service-client`. We
// re-export them so admin-web screens never have to know which package
// owns the contract, and so the admin-only screen list stays in one
// place (RBAC is enforced server-side; the UI only hides).

import type {
  BillingMode,
  MemberQuotaDefault,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  PartnerOverview,
  ServiceRole,
} from '../../shared-types/src/index.js';

export type {
  BillingMode,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  MemberQuotaDefault,
  PartnerOverview,
  ServiceRole,
};

/** The billing-mode derivation, shared with the service normalizer so the form
 *  and the stored value cannot disagree. */
export { partnerPricingBillingMode } from '../../shared-types/src/index.js';

// Re-export the full client + route table so callers can `import { ... } from './api.js'`.
import { ROUTE_TABLE, SERVICE_ROUTES } from '../../service-client/src/index.js';
export { ROUTE_TABLE, SERVICE_ROUTES };

export {
  HttpServiceClient,
  ServiceClientError,
  type AllocationBatchInput,
  type AllocationBatchResult,
  type ModelRouteAdminView,
  type GatewayBackendAdminView,
  type PartnerCustomModelDeployment,
  type PartnerCustomModelDraftTestInput,
  type PartnerCustomModelPatch,
  type PartnerCustomModelPricing,
  type PartnerCustomModelPricingInput,
  type PartnerCustomModelSummary,
  type PartnerCustomModelTestRequest,
  type PartnerCustomModelTestResult,
  type PartnerCustomModelWrite,
  type UpdatePartnerCustomModelRequest,
  type UpdatePartnerCustomModelResult,
  type CapabilitySourceRegisterInput,
  type ServiceClient,
  type ServiceClientOptions,
  type ServiceClientTransport,
} from '../../service-client/src/index.js';
export { createServiceClient } from '../../service-client/src/factory.js';

/**
 * Admin-only view of the route table. These are aliases — the canonical
 * table is `SERVICE_ROUTES` and the admin-only tests in
 * `tests/service-platform/phase3-admin-console.test.ts` assert these names
 * stay in lockstep with the canonical table. Adding a new admin route
 * means adding it to BOTH `SERVICE_ROUTES` (in service-client) and this
 * alias map.
 */
export const ADMIN_ROUTES = {
  platformDiagnosticBundlesList: SERVICE_ROUTES.platformDiagnosticBundlesList,
  platformDiagnosticBundleDownload: SERVICE_ROUTES.platformDiagnosticBundleDownload,
  platformLoginPassword: SERVICE_ROUTES.platformLoginPassword,
  tenantsList: SERVICE_ROUTES.tenantsList,
  tenantsCreate: SERVICE_ROUTES.tenantsCreate,
  tenantsDelete: SERVICE_ROUTES.tenantsDelete,
  tenantPartnerTransfer: SERVICE_ROUTES.tenantPartnerTransfer,
  platformDefaultSignupTenantGet: SERVICE_ROUTES.platformDefaultSignupTenantGet,
  platformDefaultSignupTenantPut: SERVICE_ROUTES.platformDefaultSignupTenantPut,
  platformBillingTenantSettingsGet: ROUTE_TABLE.platformBillingTenantSettingsGet,
  platformBillingTenantSettingsPatch: ROUTE_TABLE.platformBillingTenantSettingsPatch,
  platformBillingTenantProductPricePut: ROUTE_TABLE.platformBillingTenantProductPricePut,
  platformBillingTenantPromotionsCreate: ROUTE_TABLE.platformBillingTenantPromotionsCreate,
  platformBillingTenantPromotionPatch: ROUTE_TABLE.platformBillingTenantPromotionPatch,
  platformBillingTenantPromotionDelete: ROUTE_TABLE.platformBillingTenantPromotionDelete,
  platformBillingOrdersList: ROUTE_TABLE.platformBillingOrdersList,
  platformBillingOrderGet: ROUTE_TABLE.platformBillingOrderGet,
  platformBillingOrderRefundCreate: ROUTE_TABLE.platformBillingOrderRefundCreate,
  platformActAs: SERVICE_ROUTES.platformActAs,
  platformAdminsList: SERVICE_ROUTES.platformAdminsList,
  partnersList: SERVICE_ROUTES.partnersList,
  partnersCreate: SERVICE_ROUTES.partnersCreate,
  partnerGet: SERVICE_ROUTES.partnerGet,
  partnerPatch: SERVICE_ROUTES.partnerPatch,
  partnerDelete: SERVICE_ROUTES.partnerDelete,
  partnerAdminsList: SERVICE_ROUTES.partnerAdminsList,
  partnerAdminsGrant: SERVICE_ROUTES.partnerAdminsGrant,
  partnerAdminsRevoke: SERVICE_ROUTES.partnerAdminsRevoke,
  partnerModelScopeGet: SERVICE_ROUTES.partnerModelScopeGet,
  partnerModelScopeSet: SERVICE_ROUTES.partnerModelScopeSet,
  partnerCustomModelsList: SERVICE_ROUTES.partnerCustomModelsList,
  partnerCustomModelsCreate: SERVICE_ROUTES.partnerCustomModelsCreate,
  partnerCustomModelsPatch: SERVICE_ROUTES.partnerCustomModelsPatch,
  partnerCustomModelsDelete: SERVICE_ROUTES.partnerCustomModelsDelete,
  partnerCustomModelsTestProvider: SERVICE_ROUTES.partnerCustomModelsTestProvider,
  partnerCustomModelsTestLlm: SERVICE_ROUTES.partnerCustomModelsTestLlm,
  partnerMcpConnectorScopeGet: SERVICE_ROUTES.partnerMcpConnectorScopeGet,
  partnerMcpConnectorScopeSet: SERVICE_ROUTES.partnerMcpConnectorScopeSet,
  partnerCapabilitySourceScopeSet:
    SERVICE_ROUTES.partnerCapabilitySourceScopeSet,
  tenantCustomModelsSet: SERVICE_ROUTES.tenantCustomModelsSet,
  platformTenantCustomModelDeploy:
    SERVICE_ROUTES.platformTenantCustomModelDeploy,
  platformTenantCustomModelRevoke:
    SERVICE_ROUTES.platformTenantCustomModelRevoke,
  platformTenantCapabilitySourceAssign:
    SERVICE_ROUTES.platformTenantCapabilitySourceAssign,
  platformTenantCapabilitySourceRevoke:
    SERVICE_ROUTES.platformTenantCapabilitySourceRevoke,
  tenantMcpConnectorsGet: SERVICE_ROUTES.tenantMcpConnectorsGet,
  tenantMcpConnectorsSet: SERVICE_ROUTES.tenantMcpConnectorsSet,
  tenantPermissionModesSet: SERVICE_ROUTES.tenantPermissionModesSet,
  tenantDeepAgentApprovalToolsSet:
    SERVICE_ROUTES.tenantDeepAgentApprovalToolsSet,
  partnerAllocationSet: SERVICE_ROUTES.partnerAllocationSet,
  partnerAllocationGet: SERVICE_ROUTES.partnerAllocationGet,
  tenantAllocationSet: SERVICE_ROUTES.tenantAllocationSet,
  tenantAllocationGet: SERVICE_ROUTES.tenantAllocationGet,
  partnerSelfAllocationGet: SERVICE_ROUTES.partnerSelfAllocationGet,
  tenantSelfAllocationGet: SERVICE_ROUTES.tenantSelfAllocationGet,
  subjectAllocationSet: SERVICE_ROUTES.subjectAllocationSet,
  subjectAllocationBatchSet: SERVICE_ROUTES.subjectAllocationBatchSet,
  subjectAllocationGet: SERVICE_ROUTES.subjectAllocationGet,
  memberQuotaDefaultsSet: SERVICE_ROUTES.memberQuotaDefaultsSet,
  memberQuotaDefaultsGet: SERVICE_ROUTES.memberQuotaDefaultsGet,
  creditLimitsList: SERVICE_ROUTES.creditLimitsList,
  creditLimitsUpsert: SERVICE_ROUTES.creditLimitsUpsert,
  creditLimitsPreview: SERVICE_ROUTES.creditLimitsPreview,
  creditLimitsDelete: SERVICE_ROUTES.creditLimitsDelete,
  quotaProfilesList: SERVICE_ROUTES.quotaProfilesList,
  quotaProfilesCreate: SERVICE_ROUTES.quotaProfilesCreate,
  quotaProfilesUpdate: SERVICE_ROUTES.quotaProfilesUpdate,
  quotaProfilesDeactivate: SERVICE_ROUTES.quotaProfilesDeactivate,
  quotaProfilesImpactPreview: SERVICE_ROUTES.quotaProfilesImpactPreview,
  userQuotaAssignmentsList: SERVICE_ROUTES.userQuotaAssignmentsList,
  userQuotaProfileAssign: SERVICE_ROUTES.userQuotaProfileAssign,
  userQuotaProfileUnpin: SERVICE_ROUTES.userQuotaProfileUnpin,
  providerKeysRegister: SERVICE_ROUTES.providerKeysRegister,
  tenantProviderModelsBulkDeploy: SERVICE_ROUTES.tenantProviderModelsBulkDeploy,
  providerKeysRemove: SERVICE_ROUTES.providerKeysRemove,
  providerKeysTest: SERVICE_ROUTES.providerKeysTest,
  providerKeysLiteLLMTest: SERVICE_ROUTES.providerKeysLiteLLMTest,
  providerKeysList: SERVICE_ROUTES.providerKeysList,
  webSearchBackendsList: SERVICE_ROUTES.webSearchBackendsList,
  webSearchBackendsSet: SERVICE_ROUTES.webSearchBackendsSet,
  mediaRoutesList: SERVICE_ROUTES.mediaRoutesList,
  mediaRoutesSet: SERVICE_ROUTES.mediaRoutesSet,
  tenantAssignmentGet: SERVICE_ROUTES.tenantAssignmentGet,
  tenantAssignmentPut: SERVICE_ROUTES.tenantAssignmentPut,
  partnerTenantPermissionModesSet: SERVICE_ROUTES.partnerTenantPermissionModesSet,
  partnerTenantDeepAgentApprovalToolsSet: SERVICE_ROUTES.partnerTenantDeepAgentApprovalToolsSet,
  partnerCustomModelsSelfList: SERVICE_ROUTES.partnerCustomModelsSelfList,
  ratesCurrent: SERVICE_ROUTES.ratesCurrent,
  overview: SERVICE_ROUTES.overview,
  extensionReleasesList: SERVICE_ROUTES.extensionReleasesList,
  extensionReleasePromote: SERVICE_ROUTES.extensionReleasePromote,
  extensionReleaseRollback: SERVICE_ROUTES.extensionReleaseRollback,
  extensionReleaseWithdraw: SERVICE_ROUTES.extensionReleaseWithdraw,
  usersList: SERVICE_ROUTES.usersList,
  usersInvite: SERVICE_ROUTES.usersInvite,
  usersPatch: SERVICE_ROUTES.usersPatch,
  groupsList: SERVICE_ROUTES.groupsList,
  groupsCreate: SERVICE_ROUTES.groupsCreate,
  groupsPatch: SERVICE_ROUTES.groupsPatch,
  seats: SERVICE_ROUTES.seats,
  quotas: SERVICE_ROUTES.quotas,
  usageSummary: SERVICE_ROUTES.usageSummary,
  usageSeries: SERVICE_ROUTES.usageSeries,
  usageEvents: SERVICE_ROUTES.usageEvents,
  usageVerify: SERVICE_ROUTES.usageVerify,
  usageExport: SERVICE_ROUTES.usageExport,
  modelAccess: SERVICE_ROUTES.modelAccess,
  tenantLoginDefaultModelGet: SERVICE_ROUTES.tenantLoginDefaultModelGet,
  tenantLoginDefaultModelPut: SERVICE_ROUTES.tenantLoginDefaultModelPut,
  allowlist: SERVICE_ROUTES.allowlist,
  modelsPreview: SERVICE_ROUTES.modelsPreview,
  modelRoutes: SERVICE_ROUTES.modelRoutesList,
  modelRoutesCreate: SERVICE_ROUTES.modelRoutesCreate,
  customModelRemove: SERVICE_ROUTES.customModelRemove,
  gatewayBackends: SERVICE_ROUTES.gatewayBackendsList,
  gatewayBackendsCreate: SERVICE_ROUTES.gatewayBackendsCreate,
  gatewayBackendsPatch: SERVICE_ROUTES.gatewayBackendsPatch,
  routingPolicy: SERVICE_ROUTES.routingPolicy,
  routesPreview: SERVICE_ROUTES.routesPreview,
  routesHealth: SERVICE_ROUTES.routesHealth,
  policies: SERVICE_ROUTES.policiesPreview,
  audit: SERVICE_ROUTES.audit,
  downloadChannels: SERVICE_ROUTES.downloadChannelsUpdate,
  downloadLatestForUser: SERVICE_ROUTES.downloadLatestForUser,
} as const satisfies Record<string, { method: string; path: string }>;

export type {
  AdminAuditEvent,
  AdminGroupView,
  AdminOverview,
  AdminUserView,
  AllocationOverview,
  Announcement,
  AnnouncementListQuery,
  AnnouncementScope,
  AnnouncementSeverity,
  AnnouncementStatus,
  CreateAnnouncementRequest,
  PatchAnnouncementRequest,
  Paginated,
  CreditAllocation,
  CreditPurchaseOrderStatus,
  PlatformBillingOrderDetail,
  PlatformBillingOrderRow,
  PlatformCreditPromotionView,
  PlatformTenantBillingView,
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  QuotaCycle,
  QuotaScope,
  UpsertCreditLimitRequest,
  CreateQuotaProfileRequest,
  MembershipQuotaAssignment,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimit,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
  DesktopBootstrap,
  DesktopModelView,
  DesktopPlatform,
  DownloadChannel,
  DownloadManifest,
  GatewayBackend,
  GatewayCompatibility,
  Group,
  InvitedUser,
  ModelRoute,
  ModelRoutingPolicy,
  Partner,
  PartnerAdminView,
  PartnerCustomModelDefinition,
  PlatformAdminView,
  PlatformProviderKeyProvider,
  ProviderKeyRegistrationResult,
  ProviderKeyRemovalResult,
  ProviderKeySummary,
  PolicyContextSnapshot,
  QuotaPolicy,
  TenantAssignment,
  TenantMediaRoute,
  TenantMediaRouteSummary,
  TenantWebSearchBackend,
  TenantWebSearchBackendSummary,
  CreditRateTableView,
  CreditRateSettingsView,
  ServiceUsageRateKind,
  UsageChainVerification,
  UsageEvent,
  UsageEventView,
  UsageKind,
  UsageQuery,
  CreditSettlementQuery,
  CreditSettlementReport,
  UsageSeriesPoint,
  UsageSeriesQuery,
  UsageSummaryView,
  ServiceUser,
  Tenant,
  TenantModelAccess,
  WebSearchNativeProvider,
  VelugaModelCatalogItem,
} from '../../shared-types/src/index.js';
export type { MergePolicyInput } from '../../policy-service/src/merge.js';

// ── admin-only screen list ──────────────────────────────────────────────

/** Operational screens. No marketing page — the first screen is the dashboard.
 *
 *  Order matters twice over: it is the sidebar order (via the registry, whose
 *  order this must stay in lockstep with) and it decides which screens share a
 *  console tab strip — the shell groups CONSECUTIVE screens of the same registry
 *  `section`, so a section's screens must stay adjacent here. The monitoring
 *  console is Overview/Usage/Audit plus Announcements: the first three are
 *  read-only and the fourth is read-only for an auditor or billing admin, which
 *  is why those roles reach exactly this console. */
export const ADMIN_SCREENS = [
  'Platform Console',
  'Partner',
  'Overview',
  'Usage',
  'Audit',
  'Announcements',
  'Users',
  'Groups',
  'Models',
  'Default Model',
  'Extension Apps',
  'Allocations',
  'Usage Limits',
] as const;
export type AdminScreen = (typeof ADMIN_SCREENS)[number];

/** The Platform console is platform-scoped ONLY. A tenant owner/admin must not
 *  see it — their tenant-scoped token is rejected by the `platform.*` RBAC
 *  surface (403), so exposing the screen only produces failing requests. It is
 *  reachable solely through a platform-admin session. */
/** Platform-scoped consoles. Neither is reachable with a tenant token — their
 *  `platform.*` RBAC surface rejects it (403) — so they are shown only to a
 *  platform-admin session. */
const PLATFORM_ONLY_SCREENS: AdminScreen[] = [
  'Platform Console',
];
/** The partner console — shown only to a partner-admin session (its `partner.*`
 *  surface rejects platform/tenant tokens). */
const PARTNER_ONLY_SCREENS: AdminScreen[] = ['Partner'];
const TENANT_ADMIN_SCREENS: AdminScreen[] = ADMIN_SCREENS.filter(
  (screen) =>
    !PLATFORM_ONLY_SCREENS.includes(screen) &&
    !PARTNER_ONLY_SCREENS.includes(screen)
);
const NAVIGABLE_ADMIN_SCREENS: AdminScreen[] = [
  'Platform Console',
  'Partner',
  'Overview',
  'Usage',
  'Audit',
  'Announcements',
  'Users',
  'Groups',
  'Models',
  'Default Model',
  'Extension Apps',
  'Allocations',
  'Usage Limits',
];

/** Screens visible to a given set of roles (UI hint only; server enforces). */
export function visibleScreensFor(roles: ServiceRole[]): AdminScreen[] {
  // A platform admin owns partner management too, so it sees the Partner
  // console (partner CRUD) alongside the platform console. The Partner screen
  // renders role-conditionally — the platform variant hosts partner
  // management; a partner admin gets its own operational console.
  if (roles.includes('platform_admin'))
    return ['Platform Console', 'Partner', ...TENANT_ADMIN_SCREENS];
  // A partner admin gets its own console plus the tenant-admin screens, which
  // it reaches by acting INTO one of its tenants (owner token) — the same
  // act-as model as a platform admin.
  if (roles.includes('partner_admin'))
    return ['Partner', ...TENANT_ADMIN_SCREENS];
  const isAdmin = roles.includes('owner') || roles.includes('admin');
  const isAuditor = roles.includes('auditor');
  const isBilling = roles.includes('billing_admin');
  if (isAdmin) return [...TENANT_ADMIN_SCREENS];
  // The read-only roles reach the whole monitoring console, announcements
  // included — the screen renders its list without the write controls
  // (`visibleAnnouncementActions` returns none), so no request they can make is
  // rejected.
  if (isAuditor) return ['Overview', 'Usage', 'Audit', 'Announcements'];
  if (isBilling) return ['Overview', 'Usage', 'Audit', 'Announcements'];
  return [];
}

export function navigableScreensFor(roles: ServiceRole[]): AdminScreen[] {
  return visibleScreensFor(roles).filter((screen) =>
    NAVIGABLE_ADMIN_SCREENS.includes(screen)
  );
}
